import React, { createContext, useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Snackbar, Alert } from "@mui/material";

const API_URL = process.env.REACT_APP_API_URL;
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(localStorage.getItem("fleasing_b2b_token") || null);
  const [refreshToken, setRefreshToken] = useState(localStorage.getItem("fleasing_b2b_refresh_token") || null);
  const [user, setUser] = useState(null);
  const [initializing, setInitializing] = useState(true); // 🆕 prevent flashes during initial load

  const navigate = useNavigate();
  const location = useLocation();

  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const logout = useCallback(() => {
    localStorage.removeItem("fleasing_b2b_token");
    localStorage.removeItem("fleasing_b2b_refresh_token");
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setSuccessMessage("You have been logged out.");
    navigate("/login");
  }, [navigate]);

  const handleTokenRefresh = useCallback(async () => {
    if (!refreshToken) {
      logout();
      return false;
    }
  
    try {
      const response = await fetch(`${API_URL}/auth/refresh-token`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken }),
      });
  
      if (!response.ok) throw new Error("Failed to refresh token.");
  
      const data = await response.json();
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      localStorage.setItem("fleasing_b2b_token", data.token);
      localStorage.setItem("fleasing_b2b_refresh_token", data.refreshToken);
  
      return true;
    } catch (error) {
      setErrorMessage("Your session has expired. Please log in again.");
      logout();
      return false;
    }
  }, [refreshToken, logout]);

  const fetchUser = useCallback(async (attemptedRefresh = false) => {
    if (!token) {
      setInitializing(false);
      return;
    }
  
    try {
      const response = await fetch(`${API_URL}/auth/whoami`, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
  
      if (!response.ok) throw new Error("Invalid session. Please log in again.");
  
      const data = await response.json();
      setUser(data);
  
      const isAtLoginPage = location.pathname === "/" || location.pathname === "/login";
      if (isAtLoginPage) {
        if (data.userType === "ADMIN" || data.userType === "ADMIN_USER") {
          navigate("/admin/listings", { replace: true });
        } else if (data.userType === "BUYER") {
          navigate("/buyer/listings", { replace: true });
        }
      }
  
      setInitializing(false);
    } catch (error) {
      if (!attemptedRefresh) {
        const refreshed = await handleTokenRefresh();
        if (refreshed) {
          fetchUser(true); // Retry once after refresh
          return;
        }
      }
  
      setInitializing(false); // If refresh failed or second attempt failed
    }
  }, [token, handleTokenRefresh, location.pathname, navigate]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const forgotPassword = async (email) => {
    await fetch(`${API_URL}/auth/forgot-password`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email }),
    });
  }

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });

      if (!response.ok) throw new Error("Incorrect email or password.");

      const data = await response.json();
      setToken(data.token);
      setRefreshToken(data.refreshToken);
      setUser(data.webCompany);

      localStorage.setItem("fleasing_b2b_token", data.token);
      localStorage.setItem("fleasing_b2b_refresh_token", data.refreshToken);

      setSuccessMessage("Login successful!");
      // Redirect will happen in fetchUser()
    } catch (error) {
      setErrorMessage(error.message);
    }
  };

  return (
    <AuthContext.Provider value={{ token, user, login, forgotPassword, logout, initializing, setUser }}>
      {children}

      {/* Snackbars */}
      <Snackbar open={!!successMessage} autoHideDuration={3000} onClose={() => setSuccessMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="success" sx={{ width: "100%" }}>{successMessage}</Alert>
      </Snackbar>
      <Snackbar open={!!errorMessage} autoHideDuration={3000} onClose={() => setErrorMessage("")} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="error" sx={{ width: "100%" }}>{errorMessage}</Alert>
      </Snackbar>
    </AuthContext.Provider>
  );
};

export const useAuth = () => React.useContext(AuthContext);